import React from "react";
import * as Sentry from "@sentry/react";
import { useTheme } from "../contexts/ThemeContext";
import { AlertCircle, RefreshCw } from "lucide-react";

const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      beforeCapture={(scope) => {
        scope.setTag("errorBoundary", true);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

const ErrorFallback = ({ error, resetError }) => {
  const { themeClasses } = useTheme();

  return (
    <div
      className={`min-h-screen ${themeClasses.bg.primary} flex items-center justify-center p-4`}
    >
      <div
        className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-2xl p-8 max-w-md w-full text-center`}
      >
        <div
          className={`w-16 h-16 ${themeClasses.bg.secondary} rounded-full flex items-center justify-center mx-auto mb-6`}
        >
          <AlertCircle className={`w-8 h-8 ${themeClasses.text.accent}`} />
        </div>

        <h2 className={`text-2xl font-bold ${themeClasses.text.primary} mb-4`}>
          Oops! Something went wrong
        </h2>

        <p className={`${themeClasses.text.secondary} mb-6`}>
          We've been notified about this error and will fix it soon. You can try
          refreshing the page or contact support if the problem persists.
        </p>

        {import.meta.env.DEV && (
          <details
            className={`${themeClasses.bg.secondary} p-4 rounded-lg mb-6 text-left`}
          >
            <summary
              className={`${themeClasses.text.primary} font-medium cursor-pointer`}
            >
              Error Details (Development)
            </summary>
            <pre
              className={`${themeClasses.text.secondary} text-sm mt-2 overflow-auto`}
            >
              {error.message}
            </pre>
          </details>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={resetError}
            className={`${themeClasses.bg.button} text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:opacity-90 flex items-center justify-center`}
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Try Again
          </button>

          <button
            onClick={() => (window.location.href = "/")}
            className={`${themeClasses.bg.secondary} ${themeClasses.text.primary} border ${themeClasses.border.primary} px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:opacity-90`}
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
